import "react-dates/initialize";
import "react-dates/lib/css/_datepicker.css";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { PageHeader } from "@features/page-header/page-header";
import { PageTools } from "@features/page-tools/page-tools";
import ReactDOM from "react-dom";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import Loadable from "@react-loadable/revised";
import fp from "lodash/fp";
import moment from "moment";
import * as yup from "yup";
import axios from "axios";
import { Link, useHistory } from "react-router-dom";
import { siteMode } from "@cores/siteMode";
import AssetSearchBar from "./AssetSearchBar";
import TreeViewLayout from "@components/tree/TreeViewLayout";
import AccessDenied from "../errors/Error403";
import {
    ASSET_TYPES,
    CATEGORY_TYPES,
    getConstantValues,
    GROUPING_VIEW_TYPE,
    LIST_VIEW_TYPE,
    MEDIA_TYPES,
    PROPERTY_PANEL_TYPE,
    TABLE_LIST_TYPE,
    UNCATEGORIZED_ID,
} from "@constants";
import { closePropertyPanel, togglePropertyPanel } from "@modules/propertyPanel";
import { createOwnerLock, getAsset, updateAsset } from "@modules/asset";
import {
    bulkAttachCategoriesAsync,
    bulkDetachCategoriesAsync,
    clear,
    clearList,
    deleteAsset,
    getAssets,
    getIntegrateSearchAssets,
    increateAssetsDownloadCount,
    toggleActionButton,
    updateState,
} from "@modules/assets";
import { createDriveFolder, getFoldersList, moveFolder, updateMovedFolder } from "@modules/folderTreeView";
import getAccessHierachyView from "@utils/getAccessHierachyView";
import AssetTable from "./AssetListView/AssetTable";
import AssetCard from "./AssetCard";

import compareVersions, {
    VERSION_CM_10084_CREATE_ASSETS_WITH_COLLECTIONS,
    VERSION_CM_11210_ASSET_DUPLICATION,
    VERSION_CM_12725_ASSET_AUTO_ARCHIVE,
    VERSION_CM_12923_LIST_SEARCHBAR_FILTER,
    VERSION_CM_13766_SHARES_V3,
    VERSION_CM_14143_REQUEST,
    VERSION_CM_14205_ASSET_STARRED,
    VERSION_CM_14830_SEPARATION_OF_OWNER_AND_CREATOR,
    VERSION_CM_15199_MULTI_ASSETS_OWNER_CHANGE,
    VERSION_CM_4721_SHARES,
    VERSION_CM_5182_ASSET_DELETE_WITH_REFERENCE,
    VERSION_CM_9213_ASSET_MULTI_DOWNLOAD,
    VERSION_CM_9213_ASSET_MULTI_EXTERNAL_SHARE,
    VERSION_CM_9660_BULK_ARCHIVE_RESTORE,
    VERSION_CM_9766_COLLECTIONS,
} from "@cores/version";
import VersionedComponent from "@components/VersionedComponent";
import withSearchQueryParamsContext from "@components/params/withSearchQueryParamsContext";
import { loadViewInfo, saveViewInfo } from "@cores/viewStorage";
import { DRIVE_FOLDER_SEARCH_TYPE } from "../../constants";
import ListTools from "../../features/list-tools/list-tools";
import { AddPrimaryIcon } from "@components/icons";
import {
    IconAdd,
    IconArchive,
    IconCircle,
    IconCopy,
    IconDelete,
    IconDownload,
    IconFileCopyto,
    IconMove,
    IconStar,
    IconStarOutline,
} from "@mzc-cloudplex/icons";
import DownloadInfoMessage from "@routes/share/ui/DownloadInfoMessage";
import { listAssetExtensionsAPI, moveAssetsFolderAPI } from "@modules/apis/assets";
import menuService from "@services/menuService";
import { changeSelectedByList, changeSelectedItem } from "@modules/categoryTreeView";
import { useLocation } from "react-router";
import { setNotification } from "@modules/notification";
import store from "@store/store";
import TooltipButton from "@components_v2/tooltip-button/tooltip-button";
import { createDownloadJob, getDownloadJobs } from "@modules/downloadJobs";
import { listJobs } from "@modules/bulkJobs";
import { Pagination } from "@components_v2/pgaination/pagination";
import DialogCreateAsset from "@routes/asset/dialog-create";
import DialogArchive from "src/features/dialog/dialog-archive";
import { Box, Button, CircularProgress, Divider, Grid, ListItemButton, ListItemText, Typography } from "@mzc-pdc/ui";
import BreadcrumbsWithRouterPath from "../../containers/BreadcrumbsWithRouterPath/BreadcrumbsWithRouterPath";
import DialogCreateCollection from "@routes/collections/dialog-create";
import ListDragPreview from "@components/Preview/ListDragPreview";
import useSettings from "@hooks/useSettings";
import ListViewSelectButton from "@components_v2/list-view-select-button/list-view-select-button";
import DialogShare from "@features/dialog/share/dialog-share";
import DialogAssetDuplicate from "@routes/asset/dialog-asset-duplicate";
import useArchive from "@hooks/useArchive";
import useCheckPermission from "@hooks/useCheckPermission";
import { alpha } from "@mui/material";
import theme from "@styles/theme";
import useFolder from "@hooks/useFolder";
import { getContentSchemasAPI } from "@modules/apis/content-schemas";
import useVirtualFolderPath from "@hooks/useVirtualFolderPath";
import CustomMenuBreadcrumb from "@components_v2/breadcrumb/custom-menu-breadcrumb";
import countDaysBetweenDates from "@cores/countDaysBetweentDates";
import dayjs from "dayjs";
import { bulkAddFavoriteAPI, bulkRemoveFavoriteAPI } from "@modules/apis/favorite";
import { DialogRequest, DialogRequestDownload } from "@routes/request/dialog-request";
import useDownloadJobs from "@hooks/useDownloadJobs";
import DialogChangeOwner from "@routes/asset/dialog-change-owner";
import CollapsibleSplitTwoTierGridLeft from "@components_v2/grid/collapsible-split-two-tier-grid-left";

const LoadableDeleteModal = Loadable({
    loader: () => import("@components/modals/DeleteConfirm"),
    loading: () => null,
});

const LoadableDeleteAssetWithReferenceModal = Loadable({
    loader: () => import("./ui/DeleteAssetWithReferenceModal"),
    loading: () => null,
});

const LoadableSelectCategoriesModal = Loadable({
    loader: () => import("@components/modals/SelectCategories"),
    loading: () => null,
});

const LoadableSelectFolderPathModal = Loadable({
    loader: () => import("../../components/modals/SelectFolderPath"),
    loading: () => null,
});

const LoadableNewFolderModal = Loadable({
    loader: () => import("../../components/modals/NewFolderModal"),
    loading: () => null,
});

const LoadableDownloadLinkModal = Loadable({
    loader: () => import("@components/modals/DownloadLinkModal"),
    loading: () => null,
});

const LoadableRenameModal = Loadable({
    loader: () => import("@components/modals/Rename"),
    loading: () => null,
});

const LoadableFolderRenameModal = Loadable({
    loader: () => import("@components/modals/FolderRenameModal"),
    loading: () => null,
});

const LoadableCreatePhotoModal = Loadable({
    loader: () => import("@routes/asset/ui/CreatePhotoModal"),
    loading: () => null,
});

const LoadableShareRequestModal = Loadable({
    loader: () => import("@components/modals/ShareRequestModal"),
    loading: () => null,
});

const AssetsColumns = [
    { id: "name", label: "Name", type: "text", sortable: true },
    { id: "size", label: "Size", type: "number", sortable: false },
    { id: "autoArchivedAt", label: "Auto-archive schedule date", type: "date", sortable: false },
    { id: "createdAt", label: "Created", type: "date", sortable: true },
    { id: "owner", label: "Owner", type: "user", sortable: false },
];

// TODO : Video_list와 동일한 형식, 컴포넌트화 필요
const AssetList = ({ searchQueryParams, location }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const history = useHistory();
    const { pathname } = useLocation();
    const isLibrary = siteMode.get() === "CMS";

    const { spaceId, projectId } = menuService.parse(pathname);
    const { isCardView, updateIsCardView, getSettingData, resourceSettings } = useSettings({
        type: "General",
        domain: "assets",
    });
    const { getSettingData: getSettingMenuName, resourceSettings: settingsMenuName } = useSettings({
        type: "MenuName",
        domain: "assets",
    });
    const { getVirtualFolderFullPath } = useVirtualFolderPath({});
    const ASSETS_PERMISSIONS = useCheckPermission({ scope: "Project", domain: "Assets" });
    const PHOTO_PERMISSIONS = useCheckPermission({ scope: "Project", domain: "Photo" });
    const FOLDERS_PERMISSIONS = useCheckPermission({ scope: "Project", domain: "Folders" });
    const CATEGORIES_PERMISSIONS = useCheckPermission({ scope: "Project", domain: "Categories" });
    const SHARES_PERMISSIONS = useCheckPermission({ scope: "Project", domain: "Shares" });
    const DOWNLOAD_PERMISSIONS = useCheckPermission({ scope: "Project", domain: "Download" });
    const { requestArchive } = useArchive();
    const { onClickFolderTree, onRenameFolder, currentFolderInfo } = useFolder({
        domain: "ASSET",
    });
    const isCms = siteMode.get() === "CMS";
    const { generateDownloadJobHash, requestDownloadJob } = useDownloadJobs();

    const stage = useSelector(({ stage }) => stage);
    const folderTreeView = useSelector(({ folderTreeView }) => folderTreeView);
    const folderTreeViewModal = useSelector(({ folderTreeViewModal }) => folderTreeViewModal);
    const categoryTreeView = useSelector(({ categoryTreeView }) => categoryTreeView);
    const assets = useSelector(({ assets }) => assets);

    // const [isMountPending, setIsMountPending] = useState(true);
    const [selectedItems, setSelectedItems] = useState([]);
    const [selectedItemsDragging, setSelectedItemsDragging] = useState(false);
    const [deletePanel, setDeletePanel] = useState({
        active: false,
        id: null,
        name: null,
        version: null,
    });
    const [isQuickSearch, setIsQuickSearch] = useState(true);
    const [modal, setModal] = useState("");
    const [abortSource, setAbortSource] = useState(axios.CancelToken.source());
    const [actionTarget, setActionTarget] = useState(null);
    const [isControlSticky, setIsControlSticky] = useState(false);
    const [actionPending, setActionPending] = useState({});
    const [filterOptionValues, setFilterOptionValues] = useState(null);
    const [sortBy, setSortBy] = useState({ id: null, isAsc: false });
    const [tokenPaginationInfo, setTokenPaginationInfo] = useState({ currentPage: 0, data: [] });

    const listDragPreviewRef = useRef();
    const stickyDomRef = useRef();

    const handleScroll = (e) => {
        if (!stickyDomRef.current) return;
        const boxSize = stickyDomRef.current.getBoundingClientRect();
        const isSticky = boxSize.top <= window.scrollY;

        setIsControlSticky(isSticky);
    };

    const onHandleChangeSort = (params) => {
        setSortBy({ ...sortBy, ...params });
    };

    const getFilterOptions = async (filterIds) => {
        const results = {};

        // 각 필터 ID에 대해 병렬로 처리
        await Promise.all(
            filterIds.map(async (id) => {
                switch (id) {
                    case "extensions": {
                        let extensions = [];
                        if (assets?.assetExtensions?.data?.length > 0) {
                            extensions = assets.assetExtensions.data;
                        } else {
                            const response = await listAssetExtensionsAPI(stage.id, stage.endpoint);
                            extensions = response.data;
                        }
                        results[id] = extensions.map(({ extension }) => ({
                            id: extension,
                            label: extension,
                            value: extension,
                        }));
                        break;
                    }
                    case "contentSchema": {
                        let tempResults = [];
                        let nextToken = null;
                        do {
                            const params = { status: "ACTIVE", type: "EXTENDED_METADATA", nextToken };
                            const response = await getContentSchemasAPI(stage.id, stage.endpoint, projectId, params);
                            tempResults.push(...response.data.results);
                            nextToken = response.data.nextToken;
                        } while (nextToken);
                        results[id] = tempResults.map(({ id, name }) => ({
                            id,
                            label: name,
                            value: id,
                        }));
                        break;
                    }
                }
            }),
        );

        setFilterOptionValues(results);
    };

    const getViewInfoParams = () => {
        const viewInfo = loadViewInfo("assetsViewInfo", spaceId, projectId) || {};

        if (viewInfo?.virtualFolderId && !viewInfo?.virtualDriveId) {
            delete viewInfo.virtualFolderId;
        }
        delete viewInfo?.sortBy;
        delete viewInfo?.isSortAsc;
        return viewInfo;
    };

    const initActiveViewParams = useCallback(
        (customMenuParams = {}) => {
            try {
                const inputParams = { ...searchQueryParams.params, ...customMenuParams, offset: 0 };
                const assetDefaultViewMode = resourceSettings.settings?.Asset?.defaultViewMode?.value;
                let newParams = { ...getViewInfoParams(), ...inputParams };
                let isSearch = false;
                let activeViewParams;

                if (newParams.activeView === "search") isSearch = true;

                if (assetDefaultViewMode) {
                    activeViewParams = {
                        activeView: assetDefaultViewMode !== "list" ? "hierarchy" : "list",
                        hierarchyType: assetDefaultViewMode === "list" ? "category" : assetDefaultViewMode,
                    };
                } else {
                    //Note: settings > Assets > Default View Mode가 없을 경우 리스트를 기본값으로 가져가도록 설정(Settings의 기본값과 동일하게)
                    activeViewParams = {
                        activeView: "list",
                    };
                }

                if (!newParams.activeView) {
                    newParams = { ...newParams, ...activeViewParams };
                }

                if (newParams) {
                    dispatch(
                        updateState({
                            ...assets,
                            isActiveView: newParams.activeView ?? LIST_VIEW_TYPE.LIST,
                            hierarchyType: newParams.hierarchyType,
                        }),
                    );

                    if (searchQueryParams.hierarchyType === GROUPING_VIEW_TYPE.CATEGORY) {
                        dispatch(changeSelectedByList(newParams.selectedCategoryId));
                    }
                    searchQueryParams.resetSearchQueryParams({
                        action: "replace",
                        params: newParams,
                    });

                    if (isSearch) {
                        saveAssetViewInfo({
                            ...newParams,
                            ...(getViewInfoParams().activeView ? getViewInfoParams() : activeViewParams),
                        });
                    } else saveAssetViewInfo(newParams);
                }
            } catch (error) {
                console.error(error);
            }
        },
        [spaceId, projectId, searchQueryParams.params, resourceSettings],
    );

    const onChangeSearchQueryParams = async () => {
        try {
            const categoryTreeView = mergeParams.selectedCategoryId;
            const folderViewCondition = mergeParams.virtualFolderId && mergeParams.virtualDriveId;

            if (mergeParams.activeView === LIST_VIEW_TYPE.HIERARCHY) {
                if (mergeParams.hierarchyType === GROUPING_VIEW_TYPE.CATEGORY) {
                    if (!categoryTreeView) return;
                } else {
                    if (!folderViewCondition) return;
                }
            }
            await loadAssetData(mergeParams);
        } catch (error) {
            console.error(error);
        }
    };

    const selectedCategoryIdToObject = (selectedCategoryId) => {
        const params = {};
        if (fp.isNil(selectedCategoryId) || selectedCategoryId === UNCATEGORIZED_ID) {
            params.selectedCategoryId = UNCATEGORIZED_ID;
            params.isNotCategoryIds = params.selectedCategoryId === UNCATEGORIZED_ID;
        } else {
            params.selectedCategoryId = selectedCategoryId;
            params.isNotCategoryIds = selectedCategoryId === UNCATEGORIZED_ID;
        }
        return params;
    };

    const isCustomMenu = useMemo(() => {
        if (!settingsMenuName || !settingsMenuName?.settings) return undefined;
        return settingsMenuName?.settings?.MenuName?.selectMenu?.value === "custom";
    }, [settingsMenuName]);

    const mergeParams = useMemo(() => {
        let params_ = {
            ...searchQueryParams.params,
        };
        if (compareVersions(stage.version, VERSION_CM_12725_ASSET_AUTO_ARCHIVE) >= 0)
            params_.withAutoArchiveDate = true;

        if (params_.activeView?.toLowerCase() === LIST_VIEW_TYPE.HIERARCHY) {
            if (!fp.isNil(params_.hierarchyType))
                params_.hierarchyType = params_.hierarchyType ?? GROUPING_VIEW_TYPE.FOLDER;
            if (params_.hierarchyType === GROUPING_VIEW_TYPE.CATEGORY) {
                if (fp.isNil(params_.selectedCategoryId))
                    params_.selectedCategoryId = getViewInfoParams().selectedCategoryId;
                params_ = { ...params_, ...selectedCategoryIdToObject(params_.selectedCategoryId) };
                delete params_.virtualFolderId;
            } else {
                if (!fp.isNil(params_.selectedCategoryId)) delete params_.categoryIds;
                delete params_.selectedCategoryId;
                delete params_.isNotCategoryIds;
            }
        } else {
            // TODO: CustomMenu에서는 초기화 해줄 필요가 없음
            if (!isCustomMenu) {
                if (!fp.isNil(params_.selectedCategoryId)) delete params_.categoryIds;
                delete params_.selectedCategoryId;
                delete params_.isNotCategoryIds;
                delete params_.virtualFolderId;
            }
        }

        return params_;
    }, [spaceId, projectId, searchQueryParams.params, settingsMenuName]);

    const saveAssetViewInfo = (queryParams) => {
        saveViewInfo("assetsViewInfo", {
            stageId: spaceId,
            projectId: projectId,
            activeView: queryParams.activeView,
            hierarchyType: queryParams.hierarchyType,
            selectedCategoryId: queryParams.selectedCategoryId,
            virtualFolderId: queryParams.virtualFolderId,
            virtualDriveId: queryParams.virtualDriveId,
            isCardView: isCardView,
        });
    };

    const onClickTreeItem = (item, isList) => {
        setSelectedItems([]);

        const activeView = searchQueryParams.params.activeView || assets.activeView;
        let newParams = {};
        if (searchQueryParams.params.hasOwnProperty("enableFolderView"))
            newParams.enableFolderView = searchQueryParams.params.enableFolderView;
        if (searchQueryParams.params.hasOwnProperty("enableCategoryView"))
            newParams.enableCategoryView = searchQueryParams.params.enableCategoryView;
        if (compareVersions(stage.version, VERSION_CM_12725_ASSET_AUTO_ARCHIVE) >= 0) {
            newParams.withAutoArchiveDate = true;
        }
        if (item.hierarchyType !== GROUPING_VIEW_TYPE.FOLDER) {
            const categoryTreeView = store.getState().categoryTreeView;
            newParams = {
                ...newParams,
                categoryGroupId: item.id !== UNCATEGORIZED_ID && item.isRoot ? item.id : "",
                selectedCategoryId: item.id,
                isNotCategoryIds: item.id === UNCATEGORIZED_ID,
                limit: searchQueryParams.params.limit,
                offset: 0,
                hierarchyType: item.hierarchyType,
                activeView,
            };
            if (item.id === UNCATEGORIZED_ID) {
                searchQueryParams.params.categoryGroupId = categoryTreeView.data?.[0]?.id ?? "";
            }

            dispatch(
                changeSelectedItem({
                    id: newParams.selectedCategoryId,
                }),
            );
        } else {
            newParams = {
                ...newParams,
                virtualDriveId: item.driveId,
                virtualFolderId: item.hierarchyType === GROUPING_VIEW_TYPE.FOLDER ? item.id : "",
                hierarchyType: item.hierarchyType,
                activeView,
            };
        }
        saveAssetViewInfo({ ...newParams });

        if (searchQueryParams.params.selectedContentSchemaId)
            newParams.selectedContentSchemaId = searchQueryParams.params.selectedContentSchemaId;

        if (activeView?.toLowerCase() === LIST_VIEW_TYPE.HIERARCHY && !isList) {
            searchQueryParams.resetSearchQueryParams({
                action: "push",
                params: newParams,
            });
        }
    };

    const onClickToggleActionButton = (asset) => {
        dispatch(
            toggleActionButton({
                id: asset.id,
                disabledCreateJob: !canCreateVideo(asset),
            }),
        );
    };

    const onChangeOffset = async (page) => {
        searchQueryParams.updateSearchQueryParams({
            action: "push",
            params: {
                offset: page,
            },
        });
    };

    const onChangeLimit = (limit) => {
        searchQueryParams.updateSearchQueryParams({
            action: "push",
            params: { offset: 0, limit },
        });
    };

    const onClickActiveView = (value) => {
        dispatch(updateState(value));
    };

    const onClickViewType = (value) => {
        if (value === isCardView) return;
        updateIsCardView(value);
    };

    const onClickRow = (id, projectId) => {
        if (isLibrary) return;
        dispatch(
            togglePropertyPanel({
                targetId: id,
                type: PROPERTY_PANEL_TYPE.ASSET,
                projectId,
            }),
        );
    };

    const onChangeSelectItem = (asset) => {
        if (selectedItems.find((item) => item.id === asset.id)) {
            setSelectedItems(selectedItems.filter((v) => v.id !== asset.id));
        } else {
            setSelectedItems([...selectedItems, asset]);
        }
    };

    const onChangeNumSelected = useCallback(
        (checkAll) => {
            if (checkAll) {
                const filteredAssetsData = assets.data?.filter((el) => el.state !== "DRAFT");
                setSelectedItems([].concat(filteredAssetsData));
            } else {
                setSelectedItems([]);
            }
        },
        [assets.data, setSelectedItems],
    );

    const onClickDeleteCategories = async (isDeleteAssets) => {
        if (isDeleteAssets) {
            for (let i = 0; i < selectedItems.length; i++) {
                const ele = selectedItems[i];
                await dispatch(deleteAsset(ele.id, ele.version));
            }
        } else {
            await dispatch(bulkDetachCategoriesAsync(selectedItems, searchQueryParams.params.categoryIds));
        }
        loadAssetData(mergeParams);
        setSelectedItems([]);
    };

    const onChangeCategoriesCopyToItems = async (categories) => {
        const items = actionTarget ? actionTarget : selectedItems;
        if (items && !items.length) throw new Error(`Invalid Parameters, Please check the attached file`);

        await dispatch(bulkAttachCategoriesAsync(items, categories));
        setSelectedItems([]);
        setActionTarget(null);
    };

    const onChangeCategoriesMoveToItems = async (categories) => {
        const items = actionTarget ? actionTarget : selectedItems;
        if (items && !items.length) throw new Error(`Invalid Parameters, Please check the attached file`);

        const prevCategories = [{ id: searchQueryParams.params.selectedCategoryId }];

        const categoryIds = [];
        categories.map((v) => {
            if (!prevCategories.map((p) => p.id).includes(v.id)) {
                if (v.id !== UNCATEGORIZED_ID) categoryIds.push({ id: v.id });
                else categoryIds.push({ id: v.id });
            }
        });

        const detachCategories = !categories.map((v) => v.id).includes(searchQueryParams.params.selectedCategoryId)
            ? prevCategories
            : null;

        if (detachCategories) {
            await dispatch(bulkDetachCategoriesAsync(items, prevCategories));
        }
        if (categoryIds.length > 0) await dispatch(bulkAttachCategoriesAsync(items, categoryIds));
    };

    const onHandleClickChangeAssetsFolder = async (path) => {
        const targets = actionTarget ?? selectedItems;
        const data = { assetIds: targets.map((item) => item.id), virtualFolder: { id: path } };

        await moveAssetsFolderAPI(spaceId, stage.endpoint, projectId, data);
        onToggleModal();
        afterMutationWorks("move folder");
    };

    const onClickOwnerLock = async (asset) => {
        try {
            const actionKey = `owner-lock-${asset.id}`;
            if (actionPending[actionKey]) return;

            setActionPending((prev) => ({ ...prev, [actionKey]: true }));

            const params = { assetIds: [asset.id] };
            await dispatch(createOwnerLock({ isLocked: asset.isOwnerLocked, params }));
            await dispatch(
                setNotification({
                    type: "success",
                    contents: asset.isOwnerLocked
                        ? t("common::msg::Asset lock has been disabled.", "Asset lock has been disabled.")
                        : t("common::msg::Asset lock has been enabled.", "Asset lock has been enabled."),
                }),
            );
            onClickRefresh();
            setActionPending((prev) => ({ ...prev, [actionKey]: false }));
        } catch (err) {
            console.log(err);
        }
    };

    const onClickDelete = async () => {
        await dispatch(deleteAsset(deletePanel.id, deletePanel.version));

        switch (assets.isActiveView?.toLowerCase()) {
            case LIST_VIEW_TYPE.LIST:
                loadAssetData(mergeParams);
                break;

            case LIST_VIEW_TYPE.HIERARCHY:
                loadAssetData(mergeParams);
                break;

            default:
                throw new TypeError("View type for assets is not set.");
        }
    };
    const onClickDeleteWithReference = async (targetItem, isEndItem, isDeleteCategory = false) => {
        if (!isDeleteCategory) {
            const r = await dispatch(deleteAsset(targetItem.id, targetItem.version));
        } else {
            await dispatch(
                bulkDetachCategoriesAsync([targetItem], [{ id: searchQueryParams.params.selectedCategoryId }]),
            );
        }
    };

    const onClickDeleteAll = async (remainItems, isDeleteCategory = false) => {
        if (!isDeleteCategory) {
            for (let i = 0; i < remainItems.length; i++) {
                const item = remainItems[i];
                const r = await dispatch(deleteAsset(item.id, item.version));
            }
        } else {
            await dispatch(
                bulkDetachCategoriesAsync(remainItems, [{ id: searchQueryParams.params.selectedCategoryId }]),
            );
        }
    };

    const onDeleteEnd = async () => {
        await loadAssetData(mergeParams);

        setSelectedItems([]);
        onToggleModal();
    };

    const loadAssetData = async (searchParam) => {
        try {
            const searchQuery = { ...searchParam };

            if (searchQuery.selectedCategoryId) {
                if (searchQuery.selectedCategoryId === UNCATEGORIZED_ID) searchQuery.categoryIds = [{ id: -1 }];
                else searchQuery.categoryIds = [{ id: searchQuery.selectedCategoryId }];
            }
            if (searchQuery.selectedContentSchemaId) {
                searchQuery.contentSchema = { id: searchQuery.selectedContentSchemaId };
            }
            if (compareVersions(stage.version, VERSION_CM_13766_SHARES_V3) >= 0) {
                searchQuery.includeShareInfo = true;
            }

            if (
                searchParam.activeView === LIST_VIEW_TYPE.SEARCH &&
                resourceSettings.settings?.Asset?.useSearchEngine?.value === "true" &&
                stage?.extensions?.["SEARCH_ENGINE_ENABLED"] === "true"
            ) {
                const offset = searchQueryParams.params.offset ?? 0;
                searchQuery.nextToken = offset > 0 ? tokenPaginationInfo.data[offset - 1] : undefined;
                searchQuery.projectIds = [{ id: projectId }];
                const results = await dispatch(getIntegrateSearchAssets(projectId, searchQuery));
                loadCallbackFunc(results?.nextToken);
            } else {
                await dispatch(getAssets(projectId, searchQuery));
            }
        } catch (e) {
            console.log(e);
        }
    };

    const loadCallbackFunc = useCallback(
        (nextToken, init) => {
            if (!nextToken) return;
            const page = init ? 0 : mergeParams?.offset;
            const copiedTokens = init ? [] : [...tokenPaginationInfo.data];
            copiedTokens[page] = nextToken ?? null;
            setTokenPaginationInfo({ currentPage: page, data: copiedTokens });
        },
        [mergeParams, tokenPaginationInfo],
    );
    const onDoubleClickRow = (asset) => {
        history.push(getLinkDestination(asset));
    };

    const onDragStartItem = (e) => {
        if (selectedItems && selectedItems.length) {
            e.dataTransfer.setDragImage(listDragPreviewRef.current, 0, 0);
            e.dataTransfer.effectAllowed = "move";

            setSelectedItemsDragging(true);
        }
    };

    const onDragEndItem = () => {
        setSelectedItemsDragging(false);
    };

    // TODO : 현재는 Only Move, 추후 변동시 Type 추가
    const onDropTreeItem = (item) => {
        if (item) {
            onChangeCategoriesMoveToItems([item]);
        }
    };

    const canCreateVideo = (asset) => {
        return (
            asset.status === "ACTIVE" &&
            asset.mediaType === MEDIA_TYPES.VIDEO &&
            asset.type !== ASSET_TYPES.DASH &&
            asset.type !== ASSET_TYPES.HLS
        );
    };

    const onClickCreateJob = (asestId) => {
        const { spaceId, projectId } = menuService.parse(location.pathname);
        const queryParameter = `assetId=${asestId}`;

        history.push(`${menuService.paths.contentsVideoCreate(spaceId, projectId)}?${queryParameter}`);
    };

    const openImageViewer = () => {
        // const { assets, location, categoryTreeView } = this.props;
        const { spaceId, projectId } = menuService.parse(location.pathname);

        const isQuickSearch = fp.get(`search.isQuickSearch`, assets);

        const composer = fp.pipe(
            fp.get(`search`),
            (object) => {
                const convertMomentToString = (dateTime) => {
                    return `${moment(`${dateTime.format("YYYY-MM-DD")} 00:00:00`)
                        .utc()
                        .format("YYYY-MM-DD")}T${moment(`${dateTime.format("YYYY-MM-DD")} 00:00:00`)
                        .utc()
                        .format("HH:mm:ss")}Z`;
                };

                const search = fp.cloneDeep(object);
                if (!isQuickSearch) {
                    delete search.keyword;
                }

                if (!fp.isEmpty(fp.get("startAt", search)) && !fp.isEmpty(fp.get("endAt", search))) {
                    search.createdAt = `${convertMomentToString(fp.get("startAt", search))}~${convertMomentToString(
                        fp.get("endAt", search),
                    )}`;
                    delete search.startAt;
                    delete search.endAt;
                }

                return search;
            },
            fp.omitBy(fp.isEmpty),
            (object) =>
                fp.pipe(
                    fp.keys,
                    fp.map((key) => {
                        const value = object[key];
                        const arrayJoin = fp.pipe(
                            fp.map((item) =>
                                fp.has([`value`], item) || fp.has([`id`], item) // item 에 value 또는 id 키가 있는지 여부
                                    ? fp.getOr(fp.get(`value`, item), `id`, item)
                                    : item,
                            ),
                            fp.join(`,`),
                        );

                        const replaceValue = fp.isArray(value) ? arrayJoin(value) : value;
                        const replaceKey = fp.cond([
                            [fp.isEqual(`keyword`), () => `q`],
                            [fp.isEqual(`mediaTypes`), () => `mediaType`],
                            [fp.T, (value) => value],
                        ]);
                        // return `${fp.isEqual(`keyword`, key) ? `q` : [key]}=${replaceValue}`
                        // return `${[replaceKey(key)]}=${replaceValue}`
                        //TODO: 한개 타입만 허용(다시 물어봐야함)
                        return `${[replaceKey(key)]}=${
                            fp.isEqual(`mediaTypes`, key)
                                ? fp.includes(`,`, replaceValue)
                                    ? fp.pipe(fp.head, fp.get(`value`))(value)
                                    : replaceValue
                                : replaceValue
                        }`;
                    }),
                    fp.join(`&`),
                )(object),
        );
        const payload = {
            hierarchy: fp.getOr({}, `hierarchy`, assets),
            isActiveView: fp.get(`isActiveView`, assets),
        };
        window.open(`${menuService.paths.imagesViewer(spaceId, projectId)}?${location?.search || ""}`);
    };

    const onClickRefresh = async (e) => {
        e?.preventDefault();
        e?.stopPropagation();
        await setTokenPaginationInfo((prevState) => ({ currentPage: 1, data: [] }));
        await loadAssetData(mergeParams);
    };

    // 폴더뷰 화살표 클릭
    const onHandleClickFolderTree = (item, isClickArrow, useNextToken, checkUseHistory = false) => {
        onClickFolderTree(item, isClickArrow, useNextToken, checkUseHistory, onClickTreeItem);
    };

    const onClickCreateNewFolder = async (name, folder) => {
        try {
            const params = { name, parent: { id: folder.id } };
            const driveId = folder.driveId;

            await dispatch(createDriveFolder(driveId, params));
            const queryParameters = {
                parentId: folder.id,
                searchType: DRIVE_FOLDER_SEARCH_TYPE.DIRECT_CHILDREN,
            };
            const headerParams = { projectId: projectId };
            await dispatch(
                getFoldersList({
                    driveId,
                    queryParameters,
                    headerParams,
                }),
            );
        } catch (error) {
            return Promise.reject(error);
        }
    };

    const handleClickRenameAsset = async (target, value) => {
        const response = await dispatch(
            updateAsset(target.id, target.version, {
                name: value,
            }),
        );
        return response;
    };

    const afterMutationWorks = (action) => {
        setSelectedItems([]);
        setActionTarget(null);
        if (action)
            dispatch(
                setNotification({
                    type: "success",
                    contents: t(`common::msg::${"{{action}} completed."}`, {
                        action: t(`common::label::${action}`, action),
                    }),
                }),
            );
        onClickRefresh();
    };

    const onToggleModal = (key, value, actionTarget = null) => {
        if (value) {
            setModal(key);
            setActionTarget(actionTarget ? [actionTarget] : null);
        } else {
            setModal("");
            setActionTarget(null);
        }
    };

    const onHandleClickDownload = async (asset) => {
        const key = asset.id;
        try {
            const downloadRequestTargets = {
                resourceId: asset?.id,
                projectId: projectId,
                resourceName: asset?.name,
            };

            await requestDownloadJob("ASSET", downloadRequestTargets, 1, false, projectId, key);
            dispatch(increateAssetsDownloadCount([asset]));
        } catch (error) {
            console.log(error);
        }
    };

    const onHandleClickMultiDownload = async () => {
        if (!selectedItems?.length) return;

        try {
            const key = generateDownloadJobHash(selectedItems.map((item) => item.id));

            const downloadRequestTargets = {
                resourceId: selectedItems.length === 1 ? selectedItems[0].id : undefined,
                resourceIds: selectedItems.length > 1 ? selectedItems.map((item) => item.id) : undefined,
                projectId: projectId,
                resourceName: selectedItems[0].name,
            };

            await requestDownloadJob("ASSET", downloadRequestTargets, selectedItems.length, false, projectId, key);
            dispatch(increateAssetsDownloadCount(selectedItems));
            setSelectedItems([]);
        } catch (error) {
            console.log(error);
        }
    };

    const onHandleClickArchiveAsset = async (isFolder, option) => {
        try {
            setActionPending({ ...actionPending, archive: true });

            let targets = {};
            if (isFolder) {
                targets = {
                    virtualFolder: {
                        id: folderTreeView.folderList.selectedFolder.folder.id,
                        recursive: false, //TODO: 하위폴더 포함 여부 추가
                    },
                };
            } else {
                const ids = selectedItems?.length ? selectedItems.map((i) => i.id) : [actionTarget.id];
                targets = { ids };
            }
            const data = {
                archiveClass: option,
                targets,
            };
            const isMulti = selectedItems?.length > 1 ? true : false;

            await requestArchive(isMulti, data, async () => {
                dispatch(listJobs());
                await onClickRefresh();
                setSelectedItems([]);
            });
        } catch (e) {
            console.error(e);
        } finally {
            setActionPending({ ...actionPending, archive: false });
            onToggleModal();
        }
    };

    const onChangeFolderPath = useCallback(async () => {
        const selectedFolderByMenu = folderTreeView.folderList.selectedFolder.folder;

        const toFolder = folderTreeViewModal.folderList.selectedFolder.folder;
        const fromObj = { id: selectedFolderByMenu?.id, parent: { id: selectedFolderByMenu?.parent } };
        const toObj = { id: toFolder.id, parent: { id: toFolder.parent } };
        const params = { from: fromObj, to: toObj };
        const driveId = { driveId: selectedFolderByMenu?.driveId };

        try {
            if (toFolder.id !== fromObj.id) {
                await dispatch(moveFolder(driveId, params));

                dispatch(
                    setNotification({
                        type: "success",
                        contents: t(`common::msg::${"Move completed from {{item1}} to {{item2}}."}`, {
                            item1: selectedFolderByMenu.name,
                            item2: toFolder.name,
                        }),
                    }),
                );
            }
            const folderList = folderTreeView.folderList;

            if (!(folderList.selectedFolder.folder.id === fromObj.id && !folderList.data[toFolder.id])) {
                await dispatch(updateMovedFolder({ fromPosition: fromObj, toPosition: toObj }));
            } else {
                //Note: 현재 선택한 폴더를 이동하는데 트리뷰에 아직 정보가 없는 폴더의 경우 루트폴더로 이동

                const drive = folderTreeView.driveList.data?.find((el) => el.resourceType?.type === "ASSET");
                const rootFolder = folderList?.data?.[drive.rootFolder.id];
                onHandleClickFolderTree(rootFolder);
            }

            onToggleModal();
        } catch (error) {
            const errorData = error && error.response && error.response.data;
            const errorMessage = errorData && errorData.message;
            const errorStatus = (error && error.response && error.response.status) || "Unknown";
            const errorContents = `- ${errorMessage ?? error}`;

            dispatch(
                setNotification({
                    type: "error",
                    contents:
                        t(`common::msg::${"Error moving from {{item1}} to {{item2}}."}`, {
                            item1: selectedFolderByMenu.name,
                            item2: toFolder.name,
                        }) + errorContents,
                }),
            );
        }
    }, [folderTreeView, folderTreeViewModal]);

    const onHandleClickCreate = () => {
        onToggleModal("create", true);
    };

    const handleClickImageAssetToPhoto = useCallback(async () => {
        if (!selectedItems || selectedItems.length > 1) return;
        const [item] = selectedItems;

        const response = await dispatch(getAsset(item.id));
        if (!response?.data) {
            return;
        }

        const photoAsset = response.data;
        history.push(`${menuService.paths.contentsPhotoCreate(spaceId, projectId)}`, {
            photoAsset,
        });
    }, [spaceId, projectId, selectedItems]);

    const onHandleClickStarred = useCallback(
        async (resourceIds, isStarred) => {
            const pendingId = resourceIds?.length > 1 ? "bulk" : resourceIds[0].id;
            try {
                if (actionPending[`starred-${pendingId}`]) {
                    return;
                }
                setActionPending(`starred-${pendingId}`, true);

                const data = {
                    domain: "ASSET",
                    resourceIds: resourceIds,
                };

                if (isStarred) {
                    await bulkRemoveFavoriteAPI(stage.endpoint, projectId, data);
                } else {
                    await bulkAddFavoriteAPI(stage.endpoint, projectId, data);
                }
                setActionPending({ ...actionPending, [`starred-${pendingId}`]: false });
                afterMutationWorks();
            } catch (error) {
                console.error(error);
                setActionPending({ ...actionPending, [`starred-${pendingId}`]: false });
            }
        },
        [actionPending, mergeParams],
    );

    const hideContents = useMemo(() => {
        return stage && stage.extensions && stage.extensions["HIDE_CONTENTS"] === "true";
    }, [stage]);

    const getListToolsButtons = useCallback(
        (isResponsive) => {
            const isListView = assets?.isActiveView?.toLowerCase() === LIST_VIEW_TYPE.LIST;
            const isFolderView = assets?.hierarchyType === GROUPING_VIEW_TYPE.FOLDER;
            const isMoveButtonActive =
                !isListView &&
                (!isFolderView || compareVersions(stage.version, VERSION_CM_9213_ASSET_MULTI_DOWNLOAD) >= 0);
            const isCopyButtonActive =
                assets.isActiveView?.toLowerCase() === LIST_VIEW_TYPE.HIERARCHY &&
                assets.hierarchyType === GROUPING_VIEW_TYPE.CATEGORY;

            const checkDisabledByOwnerLock = selectedItems.some(
                (item) =>
                    item.isOwnerLocked &&
                    (compareVersions(stage.version, VERSION_CM_14830_SEPARATION_OF_OWNER_AND_CREATOR) < 0
                        ? item.createdBy.id
                        : item.owner?.id) !== stage?.user?.id,
            );

            const checkDisabledDownload = selectedItems.some((item) => {
                const isExpired = item?.shareInfo?.expiresAt
                    ? countDaysBetweenDates({ toDate: dayjs(item?.shareInfo?.expiresAt) }) < 0
                    : false;

                const isExceedDownloadCount =
                    item?.shareInfo?.maxDownloads > 0 &&
                    item?.shareInfo?.maxDownloads <= item?.shareInfo?.downloadCount;

                const hasDownloadPermissionByShare = item?.shareInfo && !(isExpired || isExceedDownloadCount);

                const isDisabledByOwnerLock =
                    item.isOwnerLocked &&
                    (compareVersions(stage.version, VERSION_CM_14830_SEPARATION_OF_OWNER_AND_CREATOR) < 0
                        ? item.createdBy.id
                        : item.owner?.id) !== stage?.user?.id;

                return (
                    (!item?.shareInfo || !hasDownloadPermissionByShare) &&
                    (!DOWNLOAD_PERMISSIONS.DOWNLOAD || isDisabledByOwnerLock)
                );
            });

            return [
                <VersionedComponent version={VERSION_CM_9213_ASSET_MULTI_DOWNLOAD} key={"Download"}>
                    <TooltipButton
                        circled
                        title={t("common::label::Download", "Download")}
                        buttonText={t("common::label::Download", "Download")}
                        size={"small"}
                        onClickButton={() => {
                            onHandleClickMultiDownload();
                        }}
                        buttonIcon={<IconDownload size={16} />}
                        isDivided={false}
                        isResponsive={isResponsive}
                        disabled={!DOWNLOAD_PERMISSIONS.DOWNLOAD}
                        hidden={compareVersions(stage.version, VERSION_CM_14143_REQUEST) >= 0 && checkDisabledDownload}
                    />
                </VersionedComponent>,
                <React.Fragment key={"Copy"}>
                    {isCopyButtonActive && (
                        <TooltipButton
                            circled
                            title={t("common::label::Copy", "Copy")}
                            buttonText={t("common::label::Copy", "Copy")}
                            size={"small"}
                            onClickButton={() => {
                                onToggleModal("copyCategory", true);
                            }}
                            buttonIcon={<IconCopy size={16} />}
                            isDivided={!isMoveButtonActive}
                            isResponsive={isResponsive}
                            disabled={!ASSETS_PERMISSIONS.EDIT || checkDisabledByOwnerLock}
                            hidden={isCms}
                        />
                    )}
                </React.Fragment>,
                <React.Fragment key={"Move"}>
                    {isMoveButtonActive && (
                        <TooltipButton
                            circled
                            title={t("common::label::Move", "Move")}
                            buttonText={t("common::label::Move", "Move")}
                            size={"small"}
                            onClickButton={() => {
                                if (!isFolderView) onToggleModal("moveCategory", true);
                                else onToggleModal("moveFolder", true);
                            }}
                            buttonIcon={<IconMove size={16} />}
                            isDivided={false}
                            isResponsive={isResponsive}
                            disabled={!ASSETS_PERMISSIONS.EDIT || checkDisabledByOwnerLock}
                            hidden={isCms}
                        />
                    )}
                </React.Fragment>,
                <VersionedComponent version={VERSION_CM_11210_ASSET_DUPLICATION} key={"Duplicate"}>
                    <TooltipButton
                        circled
                        title={t("common::label::Duplicate", "Duplicate")}
                        buttonText={t("common::label::Duplicate", "Duplicate")}
                        size={"small"}
                        onClickButton={() => {
                            onToggleModal("duplicate", true);
                        }}
                        buttonIcon={<IconFileCopyto size={16} />}
                        isResponsive={isResponsive}
                        disabled={!ASSETS_PERMISSIONS.EDIT || checkDisabledByOwnerLock}
                        hidden={isCms}
                    />
                </VersionedComponent>,
                <TooltipButton
                    key={"Delete"}
                    circled
                    title={t("common::label::Delete", "Delete")}
                    buttonText={t("common::label::Delete", "Delete")}
                    size={"small"}
                    onClickButton={() => {
                        onToggleModal("delete", true);
                    }}
                    buttonIcon={<IconDelete size={16} />}
                    isDivided={false}
                    isResponsive={isResponsive}
                    disabled={!ASSETS_PERMISSIONS.EDIT || checkDisabledByOwnerLock}
                    hidden={isCms}
                />,
                <VersionedComponent version={VERSION_CM_13766_SHARES_V3} key={"Request"}>
                    <TooltipButton
                        circled
                        title={t(`common::label::Request`, `Request`)}
                        buttonText={t(`common::label::Request`, `Request`)}
                        size={"small"}
                        onClickButton={() => {
                            onToggleModal("request", true);
                        }}
                        buttonIcon={<IconDownload size={16} />}
                        isDivided={false}
                        isResponsive={isResponsive}
                        hidden={!isCms}
                    />
                </VersionedComponent>,
                <VersionedComponent version={VERSION_CM_14205_ASSET_STARRED} key={"Starred"}>
                    <TooltipButton
                        circled
                        title={t(`common::label::Starred`, `Starred`)}
                        buttonText={t(`common::label::Starred`, `Starred`)}
                        size={"small"}
                        onClickButton={() => {
                            onHandleClickStarred(
                                selectedItems.map((item) => item.id),
                                false,
                            );
                        }}
                        buttonIcon={<IconStar size={16} />}
                        isDivided={false}
                        isResponsive={isResponsive}
                        disabled={!ASSETS_PERMISSIONS.READ}
                        hidden={!isCms || !selectedItems.some((item) => !item.myFavorite)}
                    />
                </VersionedComponent>,
                <VersionedComponent version={VERSION_CM_14205_ASSET_STARRED} key={"Unstarred"}>
                    <TooltipButton
                        circled
                        title={t(`common::label::Unstarred`, `Unstarred`)}
                        buttonText={t(`common::label::Unstarred`, `Unstarred`)}
                        size={"small"}
                        onClickButton={() => {
                            onHandleClickStarred(
                                selectedItems.map((item) => item.id),
                                true,
                            );
                        }}
                        buttonIcon={<IconStarOutline size={16} />}
                        isDivided={false}
                        isResponsive={isResponsive}
                        disabled={!ASSETS_PERMISSIONS.READ}
                        hidden={!isCms || !selectedItems.some((item) => item.myFavorite)}
                    />
                </VersionedComponent>,
            ];
        },
        [assets, selectedItems, stage, ASSETS_PERMISSIONS, isCms, onHandleClickMultiDownload],
    );

    const getListToolsPopperButtons = useCallback(() => {
        const checkDisabledByOwnerLock = selectedItems.some(
            (item) =>
                item.isOwnerLocked &&
                (compareVersions(stage.version, VERSION_CM_14830_SEPARATION_OF_OWNER_AND_CREATOR) < 0
                    ? item.createdBy.id
                    : item.owner?.id) !== stage?.user?.id,
        );

        return [
            [
                <ListItemButton
                    key="createCollection"
                    onClick={() => {
                        onToggleModal("createCollection", true);
                    }}
                    disabled={!ASSETS_PERMISSIONS.EDIT || checkDisabledByOwnerLock}
                    hidden={compareVersions(stage.version, VERSION_CM_9766_COLLECTIONS) < 0 || isCms}
                >
                    <ListItemText
                        primary={t("common::label::Create {{name}}", "Create {{name}}", {
                            name: t(`common::label::Collection`, `Collection`),
                        })}
                    />
                </ListItemButton>,
                <ListItemButton
                    key="shareLink"
                    onClick={() => {
                        onToggleModal("shareLink", true);
                    }}
                    disabled={!SHARES_PERMISSIONS.EXTERNAL || checkDisabledByOwnerLock}
                    hidden={compareVersions(stage.version, VERSION_CM_9213_ASSET_MULTI_EXTERNAL_SHARE) < 0 || isCms}
                >
                    <ListItemText primary={t("common::label::Share link", "Share link")} />
                </ListItemButton>,
                <ListItemButton
                    key="archive"
                    onClick={() => {
                        onToggleModal("archive", true);
                    }}
                    disabled={!ASSETS_PERMISSIONS.ARCHIVE || checkDisabledByOwnerLock}
                    hidden={compareVersions(stage.version, VERSION_CM_9660_BULK_ARCHIVE_RESTORE) < 0 || isCms}
                >
                    <ListItemText primary={t("common::label::Archive", "Archive")} />
                </ListItemButton>,
                <ListItemButton
                    key="request"
                    onClick={() => {
                        onToggleModal("request", true);
                    }}
                    disabled={!SHARES_PERMISSIONS.INTERNALREQUEST}
                    hidden={compareVersions(stage.version, VERSION_CM_14143_REQUEST) < 0 || isCms}
                >
                    <ListItemText primary={t(`common::label::Request`, `Request`)} />
                </ListItemButton>,
                <ListItemButton
                    key="starred"
                    onClick={() => {
                        onHandleClickStarred(
                            selectedItems.map((item) => item.id),
                            false,
                        );
                    }}
                    disabled={!ASSETS_PERMISSIONS.READ}
                    hidden={
                        compareVersions(stage.version, VERSION_CM_14205_ASSET_STARRED) < 0 ||
                        !selectedItems.some((item) => !item.myFavorite) ||
                        isCms
                    }
                >
                    <ListItemText primary={t(`common::label::Starred`, `Starred`)} />
                </ListItemButton>,
                <ListItemButton
                    key="unstarred"
                    onClick={() => {
                        onHandleClickStarred(
                            selectedItems.map((item) => item.id),
                            true,
                        );
                    }}
                    disabled={!ASSETS_PERMISSIONS.READ}
                    hidden={
                        compareVersions(stage.version, VERSION_CM_14205_ASSET_STARRED) < 0 ||
                        !selectedItems.some((item) => item.myFavorite) ||
                        isCms
                    }
                >
                    <ListItemText primary={t(`common::label::Unstarred`, `Unstarred`)} />
                </ListItemButton>,
            ],
            [
                <ListItemButton
                    key="ChangeOwner"
                    onClick={() => {
                        onToggleModal("changeOwner", true);
                    }}
                    hidden={
                        compareVersions(stage.version, VERSION_CM_15199_MULTI_ASSETS_OWNER_CHANGE) < 0 ||
                        (!stage.user.isSpaceAdmin && !stage.projects.some((p) => p.id === projectId && p.isAdmin))
                    }
                >
                    <ListItemText primary={t(`common::label::Change Owner`, `Change Owner`)} />
                </ListItemButton>,
            ],
        ];
    }, [ASSETS_PERMISSIONS, stage, selectedItems, isCms]);

    const filteredAssetData = useMemo(() => {
        if (!assets?.data) return [];

        const filtered = assets?.data?.filter(({ archiveStatus }) => archiveStatus !== "REQUEST_ARCHIVE");
        const type = AssetsColumns.find((s) => s.id === sortBy?.id)?.type;
        const direction = sortBy?.isAsc ? 1 : -1;

        // Sort the filtered data
        return filtered.sort((a, b) => {
            const id = sortBy?.id;
            if (id === "createdAt") {
                return direction * (a.createdAt?.localeCompare(b.createdAt) || 0);
            } else {
                if (type === "number") {
                    return direction * ((a[id] || 0) - (b[id] || 0));
                }
                return (
                    direction *
                    ((a[id]?.toLowerCase() || "").localeCompare(b[id]?.toLowerCase() || "", undefined, {
                        numeric: true,
                        sensitivity: "base",
                    }) || 0)
                );
            }
        });
    }, [assets, sortBy]);

    const pagination = useMemo(() => {
        if (
            mergeParams.activeView === LIST_VIEW_TYPE.SEARCH &&
            resourceSettings.settings?.Asset?.useSearchEngine?.value === "true"
        ) {
            return (
                <Pagination
                    totalCount={assets.totalCount}
                    page={mergeParams?.offset}
                    limitOptions={[10, 20, 50, 100, 150, 200]}
                    limit={mergeParams?.limit}
                    pageCount={assets.data?.length}
                    onChangePage={onChangeOffset}
                    onChangeLimit={onChangeLimit}
                    onClickRefresh={onClickRefresh}
                    hasNextToken={tokenPaginationInfo.data[mergeParams.offset]}
                />
            );
        } else {
            return (
                <Pagination
                    totalCount={assets.totalCount}
                    page={mergeParams?.offset / mergeParams?.limit}
                    limit={mergeParams?.limit}
                    pageCount={assets.data?.length}
                    onChangePage={(page) => onChangeOffset(page * mergeParams.limit)}
                    onChangeLimit={onChangeLimit}
                    onClickRefresh={onClickRefresh}
                    hasNextToken={mergeParams.offset < assets.totalCount - mergeParams?.limit}
                />
            );
        }
    }, [assets, mergeParams, filteredAssetData, resourceSettings, tokenPaginationInfo]);

    const assetBreadcrumbMenu = useMemo(() => {
        if (isCms) return [];

        return [
            {
                label: "New Folder",
                onClick: () => onToggleModal("createFolder", true),
                disabled: assets.hierarchyType !== GROUPING_VIEW_TYPE.FOLDER,
                buttonDisabled: !FOLDERS_PERMISSIONS.EDIT,
                clickClose: true,
            },
            {
                label: "Move",
                onClick: () => {
                    if (assets.hierarchyType === GROUPING_VIEW_TYPE.FOLDER) onToggleModal("moveFolder", true);
                    else onToggleModal("moveCategory", true);
                },
                clickClose: true,
                divider: assets.hierarchyType === GROUPING_VIEW_TYPE.FOLDER,
                disabled: folderTreeView.folderList.selectedFolder.folder?.isRoot,
                buttonDisabled: !ASSETS_PERMISSIONS.EDIT,
            },
            {
                label: "Rename",
                onClick: () => onToggleModal("renameFolder", true, folderTreeView.folderList.selectedFolder.folder),
                clickClose: true,
                disabled:
                    folderTreeView.folderList.selectedFolder.folder?.isRoot ||
                    assets.hierarchyType !== GROUPING_VIEW_TYPE.FOLDER,
                buttonDisabled: !FOLDERS_PERMISSIONS.EDIT,
            },
            {
                label: "Create Collection",
                onClick: () => onToggleModal("createCollectionFolder", true),
                clickClose: true,
                disabled:
                    folderTreeView.folderList.selectedFolder.folder?.isRoot ||
                    assets.hierarchyType !== GROUPING_VIEW_TYPE.FOLDER,
                buttonDisabled: !ASSETS_PERMISSIONS.EDIT,
            },
            {
                label: "Archive",
                onClick: () => onToggleModal("archiveFolder", true),
                clickClose: true,
                divider: true,
                disabled:
                    folderTreeView.folderList.selectedFolder.folder?.isRoot ||
                    assets.hierarchyType !== GROUPING_VIEW_TYPE.FOLDER ||
                    compareVersions(stage.version, VERSION_CM_9660_BULK_ARCHIVE_RESTORE) < 0,
                buttonDisabled: !ASSETS_PERMISSIONS.ARCHIVE,
            },
        ];
    }, [
        folderTreeView.folderList.selectedFolder.folder,
        assets,
        stage,
        ASSETS_PERMISSIONS,
        FOLDERS_PERMISSIONS,
        CATEGORIES_PERMISSIONS,
    ]);

    // 뷰 정보에서 정렬 상태를 초기화하는 함수를 분리
    const initializeSortState = () => {
        if (!spaceId || !projectId) return;
        const viewInfo = loadViewInfo("assetsViewInfo", spaceId, projectId);
        const { sortBy, isSortAsc } = viewInfo || {};

        return {
            id: sortBy || null,
            isAsc: isSortAsc || false,
        };
    };

    const isThumbnailCropped = useMemo(() => {
        return resourceSettings.settings?.Asset?.thumbnailCropOption?.value === "true";
    }, [resourceSettings]);

    const hiddenSearchBarParams = useMemo(() => {
        if (isCustomMenu === undefined) return null;
        let hiddenParams = [];
        if (isCustomMenu) {
            if (searchQueryParams.params.selectedContentSchemaId) hiddenParams.push("contentSchema");
            else if (searchQueryParams.params.selectedCategoryId) hiddenParams.push("categoryIds");
            else if (searchQueryParams.params.virtualFolderId) hiddenParams.push("location");
            return hiddenParams.length > 0 ? hiddenParams : null;
        }
        return hiddenParams;
    }, [isCustomMenu, searchQueryParams.params]);

    const hiddenSearchFilterParams = useMemo(() => {
        if (isCustomMenu === undefined) return null;
        let hiddenParams = [];
        if (isCustomMenu) {
            if (searchQueryParams.params.selectedContentSchemaId) hiddenParams.push("contentSchema");
            else if (searchQueryParams.params.selectedCategoryId) hiddenParams.push("categoryIds");
            else if (searchQueryParams.params.virtualFolderId) {
                hiddenParams.push("location");
                if (window.location.hostname.includes("lxhausys.")) {
                    hiddenParams.push("categoryIds");
                }
            }
            return hiddenParams;
        }
        return hiddenParams;
    }, [isCustomMenu, searchQueryParams.params]);

    const initCustomMenuParams = useCallback(
        (params) => {
            try {
                let inputParams = { ...(params ?? searchQueryParams.params), offset: 0 };

                let query = location.search;
                if (query) {
                    if (query.startsWith("?")) {
                        query = query.slice(1);
                    }

                    const parseValue = (decodedValue) => {
                        try {
                            const parsedValue = JSON.parse(decodedValue);
                            return parsedValue;
                        } catch (e) {
                            return decodedValue;
                        }
                    };

                    let result = {};
                    // '&'로 나눠서 각 key=value 처리
                    query.split("&").forEach((pair) => {
                        if (pair) {
                            const [key, value] = pair.split("=");
                            const decodedValue = decodeURIComponent(value);
                            const parsedValue = parseValue(decodedValue);
                            if (typeof parsedValue === "object") result = { ...result, ...parsedValue };
                            else result = { ...result, [key]: decodedValue };
                        }
                    });

                    if (!["search", "list"].includes(inputParams.activeView)) {
                        if (result.enableCategoryView || result.enableFolderView) {
                            result.activeView = "hierarchy";
                            if (result.enableFolderView) result.hierarchyType = "folder";
                            else result.hierarchyType = "category";
                        }
                    }

                    inputParams = { ...inputParams, ...result };

                    dispatch(
                        updateState({
                            ...inputParams,
                        }),
                    );
                    searchQueryParams.resetSearchQueryParams({
                        action: "push",
                        params: inputParams,
                    });
                }
            } catch (error) {
                console.log(error);
            }
        },
        [settingsMenuName],
    );

    const isQueryParamsReady = useMemo(() => {
        if (!ASSETS_PERMISSIONS.READ || !resourceSettings.settings || !settingsMenuName.settings) return false;
        if (isCustomMenu) {
            if (
                !(
                    mergeParams.selectedCategoryId ||
                    mergeParams.selectedContentSchemaId ||
                    mergeParams.virtualFolderId ||
                    mergeParams.hasOwnProperty("enableFolderView") ||
                    mergeParams.hasOwnProperty("enableCategoryView")
                )
            )
                return false;
        }

        if (!isCustomMenu && !mergeParams.activeView) return false;

        return true;
    }, [mergeParams, resourceSettings, settingsMenuName]);

    useEffect(() => {
        getFilterOptions(["extensions", "contentSchema"]);
        getSettingData("Asset");
        getSettingMenuName("MenuName");
        setSortBy(initializeSortState());

        sessionStorage.removeItem(`imagesViewer`);
        window.addEventListener("scroll", handleScroll);
        return () => {
            dispatch(clear());
            window.removeEventListener("scroll", handleScroll);
            dispatch(closePropertyPanel());
        };
    }, []);

    useEffect(() => {
        if (selectedItems?.length > 0) {
            setSelectedItems([]);
            dispatch(clearList());
        }
    }, [assets.isActiveView]);

    useEffect(() => {
        if (!isQueryParamsReady) return;

        if (
            (mergeParams.activeView &&
                ![LIST_VIEW_TYPE.SEARCH, LIST_VIEW_TYPE.HIERARCHY, LIST_VIEW_TYPE.LIST].includes(
                    mergeParams.activeView,
                )) ||
            (mergeParams.hierarchyType &&
                ![GROUPING_VIEW_TYPE.CATEGORY, GROUPING_VIEW_TYPE.FOLDER].includes(mergeParams.hierarchyType))
        ) {
            searchQueryParams.resetSearchQueryParams({ action: "push" });
            return;
        }
        onChangeSearchQueryParams();
    }, [mergeParams, isQueryParamsReady]);

    useEffect(() => {
        if (!resourceSettings.settings || !settingsMenuName.settings) return false;

        if (isCustomMenu) {
            initCustomMenuParams();
        } else {
            initActiveViewParams();
        }
    }, [resourceSettings, settingsMenuName]);

    const createAssetInitialValues = useMemo(() => {
        const initialValues = { initialFolder: currentFolderInfo };

        if (isCustomMenu) {
            if (
                searchQueryParams.params.hasOwnProperty("enableFolderView") ||
                searchQueryParams.params.hasOwnProperty("enableCategoryView")
            ) {
                if (mergeParams.selectedCategoryId) {
                    const selectedCategory = categoryTreeView.categoryMap[mergeParams.selectedCategoryId];
                    const selectedCategoryGroup = categoryTreeView.categoryMap[selectedCategory?.categoryGroup?.id];

                    if (selectedCategory?.id !== UNCATEGORIZED_ID && selectedCategory?.type === "category") {
                        initialValues["initialCategory"] = {
                            categoryId: selectedCategory?.id,
                            categoryName: selectedCategory?.name,
                            categoryGroupId: selectedCategoryGroup?.id,
                            categoryGroupName: selectedCategoryGroup?.name,
                        };
                    }
                }
                if (mergeParams.selectedContentSchemaId) {
                    initialValues["initialCustomMetadata"] = {
                        customMetadataId: mergeParams.selectedContentSchemaId,
                    };
                }
            } else if (mergeParams.virtualFolderId && mergeParams.virtualDriveId) {
                initialValues["initialFolder"] = {
                    folderId: mergeParams.virtualFolderId,
                    driveId: mergeParams.virtualDriveId,
                };
            } else if (mergeParams.selectedCategoryId) {
                initialValues["initialCategory"] = {
                    categoryId: mergeParams?.selectedCategoryId,
                    categoryGroupId: mergeParams?.defaultCategoryGroupId,
                };
            }
        }

        return initialValues;
    }, [
        location.pathname,
        mergeParams,
        currentFolderInfo,
        assets.isActiveView,
        assets.hierarchyType,
        categoryTreeView.categoryMap,
        settingsMenuName,
    ]);

    const getLinkDestination = useCallback(
        (asset) => {
            if (isCms) {
                if (isCustomMenu)
                    return `${menuService.paths.library.customMenuSets.assetsAssetDetail(
                        spaceId,
                        projectId,
                        asset.id,
                    )}?projectId=${asset.project?.id}`;
                else
                    return `${menuService.paths.library.assetsAssetDetail(spaceId, projectId, asset.id)}?projectId=${
                        asset.project.id
                    }`;
            } else {
                if (isCustomMenu)
                    return `${menuService.paths.customMenuSets.assetsAssetDetail(
                        spaceId,
                        projectId,
                        asset.id,
                    )}?projectId=${asset.project?.id}`;
                else
                    return `${menuService.paths.assetsAssetDetail(spaceId, projectId, asset.id)}?projectId=${
                        asset.project?.id
                    }`;
            }
        },
        [spaceId, projectId, isCustomMenu, isCms],
    );
    return (
        <React.Fragment>
            {!ASSETS_PERMISSIONS.READ || assets.error ? ( // status 저장 후 403이면 뜨도록 처리
                <AccessDenied />
            ) : (
                <>
                    {modal === "create" && (
                        <DialogCreateAsset open={true} onClose={() => onToggleModal()} {...createAssetInitialValues} />
                    )}
                    <PageHeader
                        title={
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1, height: "25px" }}>
                                {isQueryParamsReady && (
                                    <>
                                        {isCustomMenu ? (
                                            <CustomMenuBreadcrumb />
                                        ) : (
                                            <Typography variant={"h3"} color={theme.palette.greySecondary.main}>
                                                {t(`common::label::Assets`)}
                                            </Typography>
                                        )}
                                    </>
                                )}
                            </Box>
                        }
                        breadcrumb={
                            compareVersions(stage.version, VERSION_CM_12923_LIST_SEARCHBAR_FILTER) < 0 && (
                                <BreadcrumbsWithRouterPath />
                            )
                        }
                        actions={
                            !isCms &&
                            compareVersions(stage.version, VERSION_CM_12725_ASSET_AUTO_ARCHIVE) >= 0 &&
                            resourceSettings?.settings?.Asset?.autoArchiveOption?.value?.isEnabled && (
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1.5,
                                        px: 2,
                                        height: 32,
                                        mt: -1,
                                        backgroundColor: alpha(theme.palette.greySecondary[50], 0.5),
                                        borderRadius: 100,
                                    }}
                                >
                                    <IconCircle size={14} htmlColor={theme.palette.success.main} />
                                    <IconArchive size={20} htmlColor={theme.palette.greySecondary[500]} />
                                </Box>
                            )
                        }
                    />
                    <PageTools
                        search={
                            <AssetSearchBar
                                domain={TABLE_LIST_TYPE.ASSET}
                                hiddenSearchBarParams={hiddenSearchBarParams}
                                hiddenSearchFilterParams={hiddenSearchFilterParams}
                                isSearchEngine={resourceSettings.settings?.Asset?.useSearchEngine?.value === "true"}
                                projectId={projectId}
                                initialValues={{ ...searchQueryParams.params, activeView: LIST_VIEW_TYPE.SEARCH }}
                                isQuickSearch={isQuickSearch}
                                optionValues={filterOptionValues}
                                onChangeQuickSearch={() => {
                                    setIsQuickSearch(!isQuickSearch);
                                }}
                                onClickReset={() => {
                                    const viewInfo = getViewInfoParams();
                                    const params = searchQueryParams.params;
                                    if (isCustomMenu) {
                                        initCustomMenuParams({ activeView: viewInfo.activeView });
                                    } else {
                                        searchQueryParams.resetSearchQueryParams({
                                            action: "push",
                                            params: {
                                                isQuickSearch: params.isQuickSearch,
                                                limit: params.limit,
                                                activeView: viewInfo.activeView,
                                                hierarchyType: viewInfo.hierarchyType,
                                                selectedCategoryId: viewInfo.selectedCategoryId,
                                                virtualFolderId: viewInfo.virtualFolderId,
                                                virtualDriveId: viewInfo.virtualDriveId,
                                                ...(compareVersions(
                                                    stage.version,
                                                    VERSION_CM_12725_ASSET_AUTO_ARCHIVE,
                                                ) >= 0
                                                    ? { withAutoArchiveDate: true }
                                                    : {}),
                                            },
                                        });
                                    }
                                }}
                                onClickSubmit={(values) => {
                                    dispatch(closePropertyPanel());
                                    onClickActiveView({ isActiveView: LIST_VIEW_TYPE.SEARCH });
                                    searchQueryParams.resetSearchQueryParams({
                                        action: "push",
                                        params: {
                                            ...values,
                                            activeView: LIST_VIEW_TYPE.SEARCH,
                                            keyword: isQuickSearch ? values.keyword : values.name,
                                            name: isQuickSearch ? values.keyword : values.name,
                                        },
                                    });
                                }}
                                currentCategoryGroup={resourceSettings.settings?.Asset?.categoryGroupId}
                            />
                        }
                        actions={
                            <Grid container flexDirection={"row"}>
                                {!isCms &&
                                    searchQueryParams.params.activeView !== LIST_VIEW_TYPE.SEARCH &&
                                    (compareVersions(VERSION_CM_10084_CREATE_ASSETS_WITH_COLLECTIONS, stage.version) <=
                                    0 ? (
                                        <Grid>
                                            <Button
                                                hidden={siteMode.get() === "CMS"}
                                                size="medium"
                                                startIcon={<IconAdd size={20} />}
                                                variant="contained"
                                                onClick={onHandleClickCreate}
                                                disabled={!ASSETS_PERMISSIONS.EDIT}
                                            >
                                                <Typography variant="body1_700">
                                                    {t(`common::label::${("Create", "Create")}`)}
                                                </Typography>
                                            </Button>
                                        </Grid>
                                    ) : (
                                        <Grid>
                                            <Link
                                                to={`${location.pathname}/create`}
                                                hidden={siteMode.get() === "CMS"}
                                                className="btn btn-outline-primary"
                                                disabled={!ASSETS_PERMISSIONS.EDIT}
                                            >
                                                <AddPrimaryIcon />
                                                <strong>{t(`common::label::Create`, `Create`)}</strong>
                                            </Link>
                                        </Grid>
                                    ))}
                            </Grid>
                        }
                    />
                    <Divider flexItem />

                    <Grid
                        container
                        sx={{ flex: 1, overflow: `hidden`, position: `relative` }}
                        flexWrap={"nowrap"}
                        gap={3}
                    >
                        <CollapsibleSplitTwoTierGridLeft
                            gridLeftProps={{ minSize: 336, maxSize: 740, defaultSize: 336 }}
                            gridLeft={
                                searchQueryParams.params.activeView?.toLowerCase() === LIST_VIEW_TYPE.HIERARCHY && (
                                    // TODO : Dev, Demo, KBTV 분기처리, 추 후 조건 삭제
                                    <Grid item sx={{ height: "100%" }}>
                                        <TreeViewLayout
                                            popperMenu={true}
                                            driveDomain={"Asset"}
                                            resourceType={"Asset"}
                                            searchQueryParams={searchQueryParams}
                                            isCustomMenu={isCustomMenu}
                                            onDropTreeItem={onDropTreeItem}
                                            onClickTreeItem={onClickTreeItem}
                                            onClickForderTree={onHandleClickFolderTree}
                                            onClickCreateNewFolder={onClickCreateNewFolder}
                                            abortSource={abortSource}
                                            updateActiveView={onClickActiveView}
                                            onRefresh={onClickRefresh}
                                        />
                                    </Grid>
                                )
                            }
                            gridRight={
                                <Grid
                                    item
                                    xs
                                    sx={{
                                        display: `flex`,
                                        flexDirection: `column`,
                                        overflow: `hidden`,
                                        height: "100%",
                                    }}
                                >
                                    <ListTools
                                        domain={TABLE_LIST_TYPE.ASSET}
                                        isCardView={isCardView}
                                        getButtons={getListToolsButtons}
                                        pageCount={filteredAssetData?.filter(({ state }) => state !== "DRAFT")?.length}
                                        numSelected={selectedItems?.length}
                                        filter={null}
                                        onToggleViewType={onClickViewType}
                                        onChangeNumSelected={onChangeNumSelected}
                                        pagination={pagination}
                                        treeViewProps={
                                            searchQueryParams.params.activeView ||
                                            searchQueryParams.params.activeView === "search"
                                                ? {
                                                      activeView:
                                                          searchQueryParams.params.activeView || assets.isActiveView,
                                                      hierarchyType:
                                                          assets.hierarchyType ??
                                                          searchQueryParams.params.hierarchyType,
                                                      viewSelectButton: (
                                                          <ListViewSelectButton
                                                              domain={"Asset"}
                                                              isActiveView={
                                                                  searchQueryParams.params.activeView ||
                                                                  assets.isActiveView
                                                              }
                                                              updateActiveView={onClickActiveView}
                                                          />
                                                      ),
                                                      breadcrumbMenu: assetBreadcrumbMenu,
                                                      onClickFolderTree: onHandleClickFolderTree,
                                                      onClickTreeItem,
                                                      onToggleModal,
                                                  }
                                                : undefined
                                        }
                                        sortOptions={AssetsColumns.filter((el) => el.sortable)}
                                        onChangeSort={(id) => onHandleChangeSort({ id })}
                                        onChangeSortAsc={(isAsc) => onHandleChangeSort({ isAsc })}
                                        getPopperButtons={getListToolsPopperButtons}
                                    />

                                    {assets.pending || !assets.data ? (
                                        <Grid item xs sx={{ height: "100%" }}>
                                            <Grid
                                                container
                                                alignItems={"center"}
                                                justifyContent={"center"}
                                                sx={{ height: "100%" }}
                                            >
                                                <CircularProgress color="grey" />
                                            </Grid>
                                        </Grid>
                                    ) : (
                                        <React.Fragment>
                                            {selectedItems && selectedItems.length ? (
                                                <ListDragPreview
                                                    ref={listDragPreviewRef}
                                                    count={selectedItems.length}
                                                    selectedItem={selectedItems[0]}
                                                />
                                            ) : (
                                                ""
                                            )}
                                            {isCardView ? (
                                                <React.Fragment>
                                                    <Grid
                                                        container
                                                        gap={2.5}
                                                        sx={{ maxHeight: `100%`, overflowY: "auto" }}
                                                    >
                                                        {filteredAssetData?.length > 0 ? (
                                                            <React.Fragment>
                                                                {filteredAssetData?.map((v, i) => (
                                                                    //{/* new class : is-new */}
                                                                    <AssetCard
                                                                        key={v.id}
                                                                        asset={v}
                                                                        checkable={
                                                                            compareVersions(
                                                                                stage.version,
                                                                                VERSION_CM_4721_SHARES,
                                                                            ) >= 0
                                                                        }
                                                                        checked={
                                                                            (selectedItems || []).find(
                                                                                (item) => item.id === v.id,
                                                                            )
                                                                                ? true
                                                                                : false
                                                                        }
                                                                        draggable={
                                                                            getAccessHierachyView(stage.version) &&
                                                                            assets?.isActiveView?.toLowerCase() ===
                                                                                LIST_VIEW_TYPE.HIERARCHY &&
                                                                            assets?.hierarchyType ===
                                                                                GROUPING_VIEW_TYPE.CATEGORY &&
                                                                            selectedItems.includes(v)
                                                                        }
                                                                        thumbnailCrop={isThumbnailCropped}
                                                                        onDragStartItem={onDragStartItem}
                                                                        onDragEndItem={onDragEndItem}
                                                                        onChangeCheck={onChangeSelectItem}
                                                                        onClickCardCover={onClickRow}
                                                                        onClickRow={onClickRow}
                                                                        onDoubleClickRow={onDoubleClickRow}
                                                                        onClickDeleteAsset={() => {
                                                                            onToggleModal("delete", true, v);
                                                                        }}
                                                                        onChangeCategoriesCopyToItems={
                                                                            onChangeCategoriesCopyToItems
                                                                        }
                                                                        onChangeCategoriesMoveToItems={
                                                                            onChangeCategoriesMoveToItems
                                                                        }
                                                                        onClickCreateJob={onClickCreateJob}
                                                                        hiddenCreateJobButton={hideContents}
                                                                        onToggleModal={onToggleModal}
                                                                        onClickName={getLinkDestination}
                                                                        onClickDownload={onHandleClickDownload}
                                                                        onClickStarred={onHandleClickStarred}
                                                                        onClickOwnerLock={(v) => onClickOwnerLock(v)}
                                                                    />
                                                                ))}
                                                            </React.Fragment>
                                                        ) : (
                                                            <React.Fragment>
                                                                <div className={"empty"}>
                                                                    <i className={"sprite sprite-nojob"} />
                                                                    <p>
                                                                        {t(`common::label::Empty {{name}}`, {
                                                                            name: t(`common::label::Assets`),
                                                                        })}
                                                                    </p>
                                                                </div>
                                                            </React.Fragment>
                                                        )}
                                                    </Grid>
                                                </React.Fragment>
                                            ) : (
                                                <Grid
                                                    item
                                                    xs
                                                    sx={{
                                                        overflow: `hidden`,
                                                    }}
                                                >
                                                    <AssetTable
                                                        data={filteredAssetData}
                                                        columns={
                                                            resourceSettings?.settings?.Asset?.autoArchiveOption?.value
                                                                ?.isEnabled
                                                                ? AssetsColumns
                                                                : AssetsColumns.filter(
                                                                      (column) => column.id !== "autoArchivedAt",
                                                                  )
                                                        }
                                                        selectedItemsDragging={selectedItemsDragging}
                                                        selectedItems={selectedItems || []}
                                                        thumbnailCrop={isThumbnailCropped}
                                                        onDragStartItem={onDragStartItem}
                                                        onClickRow={onClickRow}
                                                        onDoubleClickRow={onDoubleClickRow}
                                                        onDragEndItem={onDragEndItem}
                                                        onChangeSelectItem={onChangeSelectItem}
                                                        onClickToggleActionButton={onClickToggleActionButton}
                                                        onClickDeleteButton={(v) => onToggleModal("delete", true, v)}
                                                        onClickCreateJob={onClickCreateJob}
                                                        onClickDownload={onHandleClickDownload}
                                                        onToggleModal={onToggleModal}
                                                        hiddenCreateJobButton={hideContents}
                                                        useAutoArchive={
                                                            resourceSettings?.settings?.Asset?.autoArchiveOption?.value
                                                                ?.isEnabled
                                                        }
                                                        onClickName={getLinkDestination}
                                                        onClickStarred={onHandleClickStarred}
                                                        onClickOwnerLock={(v) => onClickOwnerLock(v)}
                                                    />
                                                </Grid>
                                            )}
                                        </React.Fragment>
                                    )}
                                </Grid>
                            }
                        />
                    </Grid>
                </>
            )}
            {/*콜렉션 생성*/}
            {modal === "createCollection" && (
                <DialogCreateCollection
                    onClose={() => setModal("")}
                    open={modal === "createCollection"}
                    assetItems={selectedItems}
                />
            )}{" "}
            {/*콜렉션 생성(폴더 브레드크럼)*/}
            {modal === "createCollectionFolder" && (
                <DialogCreateCollection
                    onClose={() => setModal("")}
                    open={modal === "createCollectionFolder"}
                    assetFolder={folderTreeView.folderList.selectedFolder.folder}
                />
            )}
            {/* 복사 */}
            {modal === "copyCategory"
                ? ReactDOM.createPortal(
                      <LoadableSelectCategoriesModal
                          submitText={`Copy to`}
                          title={`Copy to`}
                          type={CATEGORY_TYPES.ASSET}
                          onModalClose={() => {
                              onToggleModal();
                          }}
                          onSelectedCategories={async (option) => {
                              onToggleModal();
                              await onChangeCategoriesCopyToItems(option);
                              await afterMutationWorks();
                          }}
                      />,
                      document.body,
                  )
                : ""}
            {/* 이동 */}
            {modal === "moveCategory" &&
                ReactDOM.createPortal(
                    <LoadableSelectCategoriesModal
                        submitText={`Move`}
                        title={`Move`}
                        type={CATEGORY_TYPES.ASSET}
                        onModalClose={() => {
                            onToggleModal();
                        }}
                        onSelectedCategories={async (option) => {
                            onToggleModal();
                            await onChangeCategoriesMoveToItems(option);
                            await afterMutationWorks();
                        }}
                    />,
                    document.body,
                )}
            {modal === "moveFolder" &&
                ReactDOM.createPortal(
                    <LoadableSelectFolderPathModal
                        open
                        domain={"Asset"}
                        submitText={`Move`}
                        title={`Move`}
                        onSubmit={({ folderId }) => {
                            if (selectedItems.length > 0 || actionTarget) onHandleClickChangeAssetsFolder(folderId);
                            else onChangeFolderPath();
                        }}
                        onClose={() => {
                            onToggleModal();
                        }}
                    />,
                    document.body,
                )}
            {/* 새폴더 */}
            {modal === "createFolder" &&
                ReactDOM.createPortal(
                    <LoadableNewFolderModal
                        folder={folderTreeView.folderList.selectedFolder.folder}
                        onClickClose={() => {
                            onToggleModal("createFolder");
                        }}
                        onClickSubmit={onClickCreateNewFolder}
                    />,
                    document.body,
                )}
            {modal === "share" && (
                <DialogShare domain={"ASSET"} selectedItem={actionTarget[0]} onClickClose={() => onToggleModal()} />
            )}
            {modal === "shareLink" &&
                ReactDOM.createPortal(
                    <LoadableDownloadLinkModal
                        domain={"ASSET"}
                        selectedItems={actionTarget ? actionTarget : selectedItems}
                        onClickClose={() => onToggleModal()}
                        onSubmit={() => {
                            onToggleModal();
                            afterMutationWorks();
                        }}
                    />,
                    document.body,
                )}
            {modal === "request" && (
                <DialogRequest
                    open={modal === "request"}
                    requirePermissionCheck={false}
                    projectId={projectId}
                    assets={actionTarget ?? selectedItems}
                    onClose={() => onToggleModal()}
                />
            )}
            {modal === "rename" &&
                ReactDOM.createPortal(
                    <LoadableRenameModal
                        selectedItems={actionTarget ? actionTarget : selectedItems}
                        onClickClose={() => onToggleModal()}
                        onClickSubmit={handleClickRenameAsset}
                        afterMutationWorks={afterMutationWorks}
                    />,
                    document.body,
                )}
            {modal === "archive" && (
                <DialogArchive
                    active={true}
                    onClose={() => onToggleModal()}
                    onSubmit={onHandleClickArchiveAsset}
                    actionPending={actionPending.archive}
                    data={selectedItems}
                    isMulti={selectedItems.length > 1}
                />
            )}
            {modal === "archiveFolder" && (
                <DialogArchive
                    active={true}
                    onClose={() => onToggleModal()}
                    onSubmit={onHandleClickArchiveAsset}
                    actionPending={actionPending.archive}
                    folder={folderTreeView.folderList.selectedFolder.folder}
                    isMulti={selectedItems.length > 1}
                />
            )}
            {modal === "delete" &&
                (compareVersions(VERSION_CM_5182_ASSET_DELETE_WITH_REFERENCE, stage.version) > 0 ? (
                    ReactDOM.createPortal(
                        <LoadableDeleteModal
                            name={actionTarget[0].name}
                            onClose={() => onToggleModal()}
                            onClickDelete={onClickDelete}
                        />,
                        document.body,
                    )
                ) : (
                    <>
                        {ReactDOM.createPortal(
                            <LoadableDeleteAssetWithReferenceModal
                                activeView={assets.isActiveView}
                                hierarchyCategorySelected={
                                    (isCustomMenu &&
                                        searchQueryParams.params.selectedCategoryId &&
                                        !searchQueryParams.params.activeView) ||
                                    assets.isActiveView === LIST_VIEW_TYPE.LIST ||
                                    !searchQueryParams.params.selectedCategoryId ||
                                    searchQueryParams.params.selectedCategoryId === UNCATEGORIZED_ID
                                        ? {}
                                        : searchQueryParams.params.selectedCategoryId
                                }
                                selectedItems={
                                    actionTarget
                                        ? [
                                              {
                                                  id: actionTarget[0].id,
                                                  name: actionTarget[0].name,
                                                  version: actionTarget[0].version,
                                              },
                                          ]
                                        : selectedItems.map((item) => ({
                                              id: item.id,
                                              name: item.name,
                                              version: item.version,
                                          }))
                                }
                                onClickClose={() => onToggleModal()}
                                onClickDelete={onClickDeleteWithReference}
                                onClickDeleteAll={onClickDeleteAll}
                                onDeleteEnd={onDeleteEnd}
                                isCategoryDelete={
                                    !(
                                        isCustomMenu &&
                                        searchQueryParams.params.selectedCategoryId &&
                                        !searchQueryParams.params.activeView
                                    ) &&
                                    assets.isActiveView?.toLowerCase() === LIST_VIEW_TYPE.HIERARCHY &&
                                    searchQueryParams.params.selectedCategoryId &&
                                    searchQueryParams.params.selectedCategoryId !== UNCATEGORIZED_ID
                                }
                            />,
                            document.body,
                        )}
                    </>
                ))}
            {modal === "createPhoto" &&
                ReactDOM.createPortal(
                    <LoadableCreatePhotoModal
                        selectedItems={selectedItems}
                        onClickClose={() => onToggleModal()}
                        onSubmit={() => {
                            onToggleModal();
                        }}
                    />,
                    document.body,
                )}
            {modal === "renameFolder" &&
                ReactDOM.createPortal(
                    <LoadableFolderRenameModal
                        folder={folderTreeView.folderList.selectedFolder.folder}
                        onClickClose={() => onToggleModal()}
                        onClickSubmit={onRenameFolder}
                        afterMutationWorks={afterMutationWorks}
                    />,
                    document.body,
                )}
            {modal === "requestShare" &&
                ReactDOM.createPortal(
                    <LoadableShareRequestModal
                        selectedItem={{ domain: "asset", ...actionTarget?.[0] }}
                        onClickClose={() => onToggleModal()}
                    />,
                    document.body,
                )}
            {modal === "requestDownload" && (
                <DialogRequestDownload projectId={projectId} assets={actionTarget} onClose={() => onToggleModal()} />
            )}
            {/* 복제 */}
            {modal === "duplicate" && (
                <DialogAssetDuplicate
                    isMulti={true}
                    assets={actionTarget ? actionTarget : selectedItems}
                    onClickClose={() => onToggleModal()}
                    isCustomMenu={isCustomMenu}
                    isCms={isCms}
                />
            )}
            {/* 소유자 변경 */}
            {modal === "changeOwner" && (
                <DialogChangeOwner
                    assets={actionTarget ? actionTarget : selectedItems}
                    onClose={() => onToggleModal()}
                    afterMutationWorks={afterMutationWorks}
                />
            )}
        </React.Fragment>
    );
};

let AssetList_ = AssetList;
AssetList_ = withSearchQueryParamsContext({
    addedSchema: {
        offset: yup.number().default(0),
        isQuickSearch: yup.bool().default(true),
        videoId: yup.string(),
        peopleId: yup.string(),
        extensions: yup
            .array()
            .of(yup.string().transform((value) => !fp.isEmpty(value) && value.toLowerCase()))
            .nullable()
            .default(undefined),
        ingestType: yup
            .string()
            .transform((value) => !fp.isNil(value) && value.toUpperCase())
            .oneOf(getConstantValues("ASSET_INGEST_TYPES")),
        activeView: yup.string(),
        selectedCategoryId: yup.string(),
        withAutoArchiveDate: yup.boolean(),
        enableFolderView: yup.boolean(),
        enableCategoryView: yup.boolean(),
        categoryGroup: yup.object(),
        contentSchema: yup
            .object({
                id: yup.string(),
                value: yup.string(),
                label: yup.string(),
            })
            .nullable()
            .default(undefined),
        defaultCategoryGroupId: yup.string(), //Note: Custom menu에서 카테고리 메뉴를 만들경우 생성모달에 기본값으로 전달할 카테고리 그룹을 위해 사용(categoryGroup은 목록조회 API에서 조건이 붙어 파라미터가 달라질 수 있어 사용불가)
    },
    setProps: true,
    // removeExtraParams: true,
})(AssetList_);

export default AssetList_;
